/**
 * Request and response shapes of the mai-fixed-asset web API, shared by the server (validation)
 * and the web app. Instants are ISO 8601 strings in UTC; dates are `YYYY-MM-DD`; amounts are
 * decimal strings (never JS numbers).
 */
import { AccountTitle, AssetClass, AssetEventType, AssetStatus, AttachmentCategory, BYTES_PER_KIB, DisposalMethod, InventoryResult, InventoryStatus, Tangibility, zCurrencyCode, zDecimalAmount, zIsoDate, zWholeAmount, } from '@mai-team-amusement/core';
import { z } from 'zod';
import { zAssetCode, zAssetTypePrefix } from './asset-code.js';
import { zCountryCode } from './country.js';
export * from './asset-code.js';
export * from './country.js';
export const DEFAULT_AUDIT_PAGE_SIZE = 50;
export const MAX_AUDIT_PAGE_SIZE = 200;
export const DEFAULT_ASSET_PAGE_SIZE = 50;
export const MAX_ASSET_PAGE_SIZE = 200;
/** Most asset IDs one list request may name (label printing). */
export const MAX_CODES_PER_QUERY = 200;
export const USER_SEARCH_MAX_LENGTH = 100;
export const ASSET_NAME_MAX_LENGTH = 200;
/** Maker, model, serial number, previous asset number. */
export const ASSET_TEXT_MAX_LENGTH = 200;
export const ASSET_NOTE_MAX_LENGTH = 2000;
export const ASSET_TYPE_NAME_MAX_LENGTH = 100;
export const LOCATION_NAME_MAX_LENGTH = 100;
export const LOCATION_NOTE_MAX_LENGTH = 500;
export const DISPOSAL_NOTE_MAX_LENGTH = 500;
export const USEFUL_LIFE_MAX_YEARS = 100;
const SEARCH_MAX_LENGTH = 100;
export const zInstant = z.iso.datetime();
/** Text that may be left empty: trimmed, and `''` becomes `null`. */
const zOptionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === null || value === '' ? null : value));
const zName = (max) => z.string().trim().min(1).max(max);
const zId = z.coerce.number().int().positive();
const zVersion = z.number().int().positive();
const zUsefulLife = z.number().int().min(0).max(USEFUL_LIFE_MAX_YEARS).nullable();
/** A user referenced by a record, with the name from the local directory (null if unknown). */
export const zPersonRef = z.object({ sub: z.string(), name: z.string().nullable() });
/** A person on an asset (user or manager); `active` is false once disabled in mai-uid. */
export const zPersonView = zPersonRef.extend({
    employeeNumber: z.string().nullable(),
    active: z.boolean(),
});
/** What the signed-in user may do in this system (asset manager rights are local to it). */
export const zCapabilities = z.object({
    /** Asset manager or admin: register and change assets, locations, inventory counts. */
    canManageAssets: z.boolean(),
    /** Asset managers, admins and group managers (their own group) see the asset register. */
    canListAssets: z.boolean(),
    /** Admins: asset types, site settings, asset manager rights, audit log. */
    canAdminister: z.boolean(),
});
// --- Asset managers ---------------------------------------------------------------------------
export const zAssetManagerGrantView = z.object({
    sub: z.string(),
    familyName: z.string().nullable(),
    givenName: z.string().nullable(),
    employeeNumber: z.string().nullable(),
    groupName: z.string().nullable(),
    grantedBy: zPersonRef.nullable(),
    grantedAt: zInstant,
    /** Assets (not disposed) this user is the asset manager of. */
    managedAssets: z.number().int().nonnegative(),
});
export const zAssetManagerGrantList = z.object({ managers: z.array(zAssetManagerGrantView) });
/** People who can be the asset manager of an asset: grant holders and admins. */
export const zEligibleManagerList = z.object({ managers: z.array(zPersonView) });
export const zReassignInput = z.object({ fromSub: z.uuid(), toSub: z.uuid() });
export const zReassignResult = z.object({ moved: z.number().int().nonnegative() });
export const zUserSearch = z.object({
    query: z.string().trim().min(1).max(USER_SEARCH_MAX_LENGTH),
});
// --- Sites ------------------------------------------------------------------------------------
/** A mai-uid group, with this system's settings for it as a site. */
export const zSiteView = z.object({
    groupId: z.number().int(),
    name: z.string(),
    /** ISO 3166-1 alpha-2; null until an admin sets it. */
    country: z.string().nullable(),
    /** ISO 4217; preselected when registering assets here. */
    defaultCurrency: z.string().nullable(),
    /** Assets can be registered here (country and currency are set). */
    configured: z.boolean(),
    /** In Japan: assets also need the acquisition cost in JPY. */
    requiresJpyAmount: z.boolean(),
});
export const zSiteList = z.object({ sites: z.array(zSiteView) });
export const zSiteSettingsInput = z.object({
    country: zCountryCode,
    defaultCurrency: zCurrencyCode,
});
// --- Asset types ------------------------------------------------------------------------------
export const zAssetTypeView = z.object({
    id: z.number().int(),
    name: z.string(),
    prefix: z.string(),
    accountTitle: z.enum(AccountTitle),
    defaultAssetClass: z.enum(AssetClass),
    defaultTangibility: z.enum(Tangibility),
    defaultUsefulLifeYears: z.number().int().nullable(),
    active: z.boolean(),
    /** An asset ID has been issued: the prefix can no longer change. */
    prefixLocked: z.boolean(),
    version: z.number().int(),
});
export const zAssetTypeList = z.object({ assetTypes: z.array(zAssetTypeView) });
export const zAssetTypeInput = z.object({
    name: zName(ASSET_TYPE_NAME_MAX_LENGTH),
    prefix: zAssetTypePrefix,
    accountTitle: z.enum(AccountTitle),
    defaultAssetClass: z.enum(AssetClass),
    defaultTangibility: z.enum(Tangibility),
    defaultUsefulLifeYears: zUsefulLife,
});
export const zAssetTypeUpdate = zAssetTypeInput.partial().extend({
    version: zVersion,
    active: z.boolean().optional(),
});
// --- Storage locations ------------------------------------------------------------------------
export const zLocationView = z.object({
    id: z.number().int(),
    groupId: z.number().int(),
    name: z.string(),
    notes: z.string().nullable(),
    active: z.boolean(),
    version: z.number().int(),
});
export const zLocationList = z.object({ locations: z.array(zLocationView) });
export const zLocationQuery = z.object({ groupId: zId.optional() });
export const zLocationInput = z.object({
    groupId: z.number().int().positive(),
    name: zName(LOCATION_NAME_MAX_LENGTH),
    notes: zOptionalText(LOCATION_NOTE_MAX_LENGTH),
});
export const zLocationUpdate = z.object({
    version: zVersion,
    name: zName(LOCATION_NAME_MAX_LENGTH).optional(),
    notes: zOptionalText(LOCATION_NOTE_MAX_LENGTH).optional(),
    active: z.boolean().optional(),
});
// --- Assets -----------------------------------------------------------------------------------
/** Statuses an asset can be registered with or changed to (disposal has its own endpoint). */
export const AssignableStatus = {
    InUse: AssetStatus.InUse,
    InStorage: AssetStatus.InStorage,
    UnderRepair: AssetStatus.UnderRepair,
    Lost: AssetStatus.Lost,
};
/** Statuses of a new asset (not lost, not disposed). */
export const InitialStatus = {
    InUse: AssetStatus.InUse,
    InStorage: AssetStatus.InStorage,
    UnderRepair: AssetStatus.UnderRepair,
};
const zAcquisitionFields = {
    acquiredOn: zIsoDate,
    currency: zCurrencyCode,
    amount: zDecimalAmount,
    /** Required at sites in Japan when the currency is not JPY; whole yen. */
    amountJpy: zWholeAmount.nullable(),
};
const zDescriptiveFields = {
    usefulLifeYears: zUsefulLife,
    maker: zOptionalText(ASSET_TEXT_MAX_LENGTH),
    model: zOptionalText(ASSET_TEXT_MAX_LENGTH),
    serialNumber: zOptionalText(ASSET_TEXT_MAX_LENGTH),
    previousCode: zOptionalText(ASSET_TEXT_MAX_LENGTH),
    notes: zOptionalText(ASSET_NOTE_MAX_LENGTH),
};
export const zAssetCreateInput = z.object({
    typeId: z.number().int().positive(),
    name: zName(ASSET_NAME_MAX_LENGTH),
    assetClass: z.enum(AssetClass),
    tangibility: z.enum(Tangibility),
    groupId: z.number().int().positive(),
    locationId: z.number().int().positive().nullable(),
    userSub: z.uuid().nullable(),
    managerSub: z.uuid(),
    status: z.enum(InitialStatus),
    ...zAcquisitionFields,
    ...zDescriptiveFields,
});
/** Changed fields only, plus the version the change is based on (optimistic locking). */
export const zAssetUpdateInput = z
    .object({
    name: zName(ASSET_NAME_MAX_LENGTH),
    assetClass: z.enum(AssetClass),
    tangibility: z.enum(Tangibility),
    ...zAcquisitionFields,
    ...zDescriptiveFields,
})
    .partial()
    .extend({ version: zVersion });
export const zAssetUserInput = z.object({ version: zVersion, userSub: z.uuid().nullable() });
export const zAssetManagerInput = z.object({ version: zVersion, managerSub: z.uuid() });
export const zAssetLocationInput = z.object({
    version: zVersion,
    locationId: z.number().int().positive().nullable(),
});
/** Moving to another site: a location there, and the JPY amount if the new site needs one. */
export const zAssetSiteInput = z.object({
    version: zVersion,
    groupId: z.number().int().positive(),
    locationId: z.number().int().positive().nullable(),
    amountJpy: zWholeAmount.nullable().optional(),
});
export const zAssetStatusInput = z.object({ version: zVersion, status: z.enum(AssignableStatus) });
export const zDisposalInput = z.object({
    version: zVersion,
    disposedOn: zIsoDate,
    method: z.enum(DisposalMethod),
    note: zOptionalText(DISPOSAL_NOTE_MAX_LENGTH),
});
export const zVersionInput = z.object({ version: zVersion });
/** Orders of the asset list. */
export const AssetSort = {
    Code: 1,
    Name: 2,
    AcquiredOn: 3,
    UpdatedAt: 4,
};
const zFlag = z.enum(['true', 'false']).transform((value) => value === 'true');
/** `1,2,3` → statuses. */
const zStatusList = z
    .string()
    .transform((value) => value.split(',').filter((part) => part !== ''))
    .pipe(z.array(z.string().transform(Number).pipe(z.enum(AssetStatus))));
/** `PC0000000001,PC0000000002` → asset IDs. */
const zCodeList = z
    .string()
    .transform((value) => value.split(',').filter((part) => part !== ''))
    .pipe(z.array(zAssetCode).max(MAX_CODES_PER_QUERY));
export const zAssetQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(MAX_ASSET_PAGE_SIZE)
        .default(DEFAULT_ASSET_PAGE_SIZE),
    groupId: zId.optional(),
    typeId: zId.optional(),
    accountTitle: z.coerce.number().pipe(z.enum(AccountTitle)).optional(),
    assetClass: z.coerce.number().pipe(z.enum(AssetClass)).optional(),
    tangibility: z.coerce.number().pipe(z.enum(Tangibility)).optional(),
    /** Default: every status but Disposed. */
    statuses: zStatusList.optional(),
    locationId: zId.optional(),
    userSub: z.uuid().optional(),
    managerSub: z.uuid().optional(),
    /** Asset ID, name, maker, model, serial number or previous number contains it. */
    q: z.string().trim().max(SEARCH_MAX_LENGTH).optional(),
    jpyMissing: zFlag.optional(),
    userDisabled: zFlag.optional(),
    codes: zCodeList.optional(),
    sort: z.coerce.number().pipe(z.enum(AssetSort)).default(AssetSort.Code),
    desc: zFlag.optional(),
});
export const zAssetView = z.object({
    id: z.number().int(),
    code: z.string(),
    name: z.string(),
    type: z.object({ id: z.number().int(), name: z.string(), prefix: z.string() }),
    accountTitle: z.enum(AccountTitle),
    assetClass: z.enum(AssetClass),
    tangibility: z.enum(Tangibility),
    site: z.object({ groupId: z.number().int(), name: z.string().nullable() }),
    location: z.object({ id: z.number().int(), name: z.string() }).nullable(),
    user: zPersonView.nullable(),
    manager: zPersonView,
    status: z.enum(AssetStatus),
    acquiredOn: zIsoDate,
    usefulLifeYears: z.number().int().nullable(),
    maker: z.string().nullable(),
    model: z.string().nullable(),
    serialNumber: z.string().nullable(),
    previousCode: z.string().nullable(),
    notes: z.string().nullable(),
    /** Only for asset managers, admins and the group managers of the site; else null. */
    acquisition: z
        .object({
        currency: z.string(),
        amount: z.string(),
        amountJpy: z.string().nullable(),
    })
        .nullable(),
    disposal: z
        .object({
        disposedOn: zIsoDate,
        method: z.enum(DisposalMethod),
        note: z.string().nullable(),
    })
        .nullable(),
    flags: z.object({
        /** At a site in Japan without the JPY amount (the site's country changed later). */
        jpyAmountMissing: z.boolean(),
        /** The user is disabled in mai-uid. */
        userDisabled: z.boolean(),
    }),
    version: z.number().int(),
    createdAt: zInstant,
    updatedAt: zInstant,
    /** What the signed-in user may do with this asset now. */
    permissions: z.object({ edit: z.boolean(), revertDisposal: z.boolean() }),
});
export const zAssetPage = z.object({ items: z.array(zAssetView), total: z.number().int() });
export const zAssetList = z.object({ assets: z.array(zAssetView) });
/** What anyone who opens an asset's QR link sees, so a found item can be returned. */
export const zAssetCardView = z.object({
    code: z.string(),
    name: z.string(),
    typeName: z.string(),
    siteName: z.string().nullable(),
    managerName: z.string().nullable(),
    status: z.enum(AssetStatus),
    /** The signed-in user may open the full asset page. */
    canView: z.boolean(),
});
export const zAssetEventView = z.object({
    id: z.number().int(),
    type: z.enum(AssetEventType),
    /** Changed fields before and after (amounts are left out for viewers who may not see them). */
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    actor: zPersonRef.nullable(),
    createdAt: zInstant,
});
export const zAssignmentView = z.object({
    user: zPersonView,
    assignedAt: zInstant,
    assignedBy: zPersonRef.nullable(),
    unassignedAt: zInstant.nullable(),
});
export const zAssetHistory = z.object({
    events: z.array(zAssetEventView),
    /** Periods of use, newest first. */
    assignments: z.array(zAssignmentView),
});
// --- Attachments ------------------------------------------------------------------------------
export const MAX_ATTACHMENT_MIB = 10;
export const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MIB * BYTES_PER_KIB * BYTES_PER_KIB;
export const MAX_ATTACHMENTS_PER_ASSET = 20;
export const ATTACHMENT_FILENAME_MAX_LENGTH = 255;
/** The file types that can be attached (MIME types, detected from the content). */
export const AttachmentType = {
    Jpeg: 'image/jpeg',
    Png: 'image/png',
    Webp: 'image/webp',
    Pdf: 'application/pdf',
};
/** Photos must be images; documents may be images or PDFs. */
export const PHOTO_TYPES = [
    AttachmentType.Jpeg,
    AttachmentType.Png,
    AttachmentType.Webp,
];
export const zAttachmentView = z.object({
    id: z.number().int(),
    category: z.enum(AttachmentCategory),
    filename: z.string(),
    contentType: z.enum(AttachmentType),
    sizeBytes: z.number().int(),
    uploadedAt: zInstant,
    uploadedBy: zPersonRef.nullable(),
});
export const zAttachmentList = z.object({ attachments: z.array(zAttachmentView) });
/** Upload: the file is the raw request body; these travel in the query string. */
export const zAttachmentUploadQuery = z.object({
    category: z.coerce.number().pipe(z.enum(AttachmentCategory)),
    filename: z.string().trim().min(1).max(ATTACHMENT_FILENAME_MAX_LENGTH),
});
// --- Inventory counts -------------------------------------------------------------------------
export const INVENTORY_TITLE_MAX_LENGTH = 100;
export const INVENTORY_NOTE_MAX_LENGTH = 500;
export const zInventoryStartInput = z.object({
    groupId: z.number().int().positive(),
    title: zName(INVENTORY_TITLE_MAX_LENGTH),
    /** Some storage locations of the site; null counts the whole site. */
    locationIds: z.array(z.number().int().positive()).min(1).nullable(),
});
export const zLocationRef = z.object({ id: z.number().int(), name: z.string() });
export const zInventoryProgress = z.object({
    total: z.number().int(),
    found: z.number().int(),
    missing: z.number().int(),
    unchecked: z.number().int(),
    /** Found somewhere other than the expected location. */
    locationDiffers: z.number().int(),
});
export const zInventoryView = z.object({
    id: z.number().int(),
    groupId: z.number().int(),
    siteName: z.string().nullable(),
    title: z.string(),
    /** Null: the whole site. */
    locations: z.array(zLocationRef).nullable(),
    status: z.enum(InventoryStatus),
    startedBy: zPersonRef.nullable(),
    startedAt: zInstant,
    closedBy: zPersonRef.nullable(),
    closedAt: zInstant.nullable(),
    progress: zInventoryProgress,
});
export const zInventoryList = z.object({ inventories: z.array(zInventoryView) });
/** One asset of a count: where and with whom it was expected, and what the check found. */
export const zInventoryItemView = z.object({
    code: z.string(),
    name: z.string(),
    typeName: z.string(),
    status: z.enum(AssetStatus),
    expectedLocation: zLocationRef.nullable(),
    expectedUser: zPersonRef.nullable(),
    result: z.enum(InventoryResult),
    foundLocation: zLocationRef.nullable(),
    locationDiffers: z.boolean(),
    checkedBy: zPersonRef.nullable(),
    checkedAt: zInstant.nullable(),
    /** Confirmed by the asset's user. */
    selfConfirmed: z.boolean(),
    note: z.string().nullable(),
});
export const zInventoryDetail = z.object({
    inventory: zInventoryView,
    items: z.array(zInventoryItemView),
});
export const zInventoryCheckInput = z.object({
    result: z.enum(InventoryResult),
    /** Where a found asset is; defaults to the expected location. Ignored for other results. */
    foundLocationId: z.number().int().positive().nullable().optional(),
    note: zOptionalText(INVENTORY_NOTE_MAX_LENGTH).optional(),
});
/** An item of an open count that the signed-in user may confirm (My assets, the asset page). */
export const zOpenInventoryItem = z.object({
    inventoryId: z.number().int(),
    title: z.string(),
    siteName: z.string().nullable(),
    code: z.string(),
    name: z.string(),
    expectedLocation: zLocationRef.nullable(),
    result: z.enum(InventoryResult),
    selfConfirmed: z.boolean(),
});
export const zOpenInventoryItemList = z.object({ items: z.array(zOpenInventoryItem) });
// --- CSV import -------------------------------------------------------------------------------
export const IMPORT_MAX_ROWS = 2000;
export const IMPORT_MAX_MIB = 5;
export const IMPORT_MAX_BYTES = IMPORT_MAX_MIB * BYTES_PER_KIB * BYTES_PER_KIB;
export const IMPORT_FILENAME_MAX_LENGTH = 255;
/** Character encodings of import files (WHATWG encoding labels). */
export const ImportEncoding = {
    Utf8: 'utf-8',
    /** Excel's "CSV" on Japanese Windows. */
    ShiftJis: 'shift_jis',
    /** Simplified Chinese Windows. */
    Gb18030: 'gb18030',
    /** Traditional Chinese Windows. */
    Big5: 'big5',
};
/** The columns of an import file (their keys; headers may also be labels in any language). */
export const ImportColumn = {
    AssetType: 'assetType',
    Name: 'name',
    AssetClass: 'assetClass',
    Tangibility: 'tangibility',
    Site: 'site',
    Location: 'location',
    UserEmployeeNumber: 'userEmployeeNumber',
    ManagerEmployeeNumber: 'managerEmployeeNumber',
    Status: 'status',
    AcquiredOn: 'acquiredOn',
    Currency: 'currency',
    Amount: 'amount',
    AmountJpy: 'amountJpy',
    UsefulLifeYears: 'usefulLifeYears',
    Maker: 'maker',
    Model: 'model',
    SerialNumber: 'serialNumber',
    PreviousCode: 'previousCode',
    Notes: 'notes',
};
/** What is wrong with a cell, a row or the header of an import file. */
export const ImportProblem = {
    Required: 'Required',
    /** Not a value of the column (date, number or label). */
    Invalid: 'Invalid',
    TooLong: 'TooLong',
    UnknownAssetType: 'UnknownAssetType',
    InactiveAssetType: 'InactiveAssetType',
    UnknownSite: 'UnknownSite',
    SiteNotConfigured: 'SiteNotConfigured',
    /** Not a storage location of the site (the import can create it). */
    UnknownLocation: 'UnknownLocation',
    InactiveLocation: 'InactiveLocation',
    UnknownEmployee: 'UnknownEmployee',
    UserDisabled: 'UserDisabled',
    NotAssetManager: 'NotAssetManager',
    StatusNotAllowed: 'StatusNotAllowed',
    FutureDate: 'FutureDate',
    InvalidCurrency: 'InvalidCurrency',
    InvalidAmount: 'InvalidAmount',
    JpyAmountRequired: 'JpyAmountRequired',
    /** More assets of the type than its asset IDs can number. */
    AssetNumberExhausted: 'AssetNumberExhausted',
    UnknownColumn: 'UnknownColumn',
    DuplicateColumn: 'DuplicateColumn',
    MissingColumn: 'MissingColumn',
};
export const zImportIssue = z.object({
    /** The row as a spreadsheet numbers it (1 = the header). */
    line: z.number().int(),
    /** The column key, or the header text of an unknown column. */
    column: z.string(),
    problem: z.enum(ImportProblem),
});
export const zImportRow = z.object({
    line: z.number().int(),
    /** The cells by column key, as they are in the file. */
    values: z.record(z.string(), z.string()),
});
export const zImportPreview = z.object({
    rows: z.array(zImportRow),
    issues: z.array(zImportIssue),
    /** Storage locations the file names that do not exist yet. */
    missingLocations: z.array(z.object({ siteName: z.string(), name: z.string() })),
});
export const zImportResult = z.object({
    /** Each line's new asset ID (and its previous number, for relabeling). */
    created: z.array(z.object({ line: z.number().int(), code: z.string(), previousCode: z.string().nullable() })),
    locationsCreated: z.number().int(),
});
/** Preview and import: the file is the raw request body; these travel in the query string. */
export const zImportQuery = z.object({
    filename: z.string().trim().min(1).max(IMPORT_FILENAME_MAX_LENGTH),
    encoding: z.enum(ImportEncoding).default(ImportEncoding.Utf8),
    /** Create the storage locations the file names that do not exist yet. */
    createLocations: zFlag.default(false),
});
// --- Audit ------------------------------------------------------------------------------------
export const zAuditQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(MAX_AUDIT_PAGE_SIZE)
        .default(DEFAULT_AUDIT_PAGE_SIZE),
});
export const zAuditEntry = z.object({
    id: z.number().int(),
    actorUserId: z.string().nullable(),
    actorName: z.string().nullable(),
    action: z.number().int(),
    targetType: z.string(),
    targetId: z.string(),
    before: z.unknown(),
    after: z.unknown(),
    ip: z.string().nullable(),
    createdAt: zInstant,
});
export const zAuditPage = z.object({ items: z.array(zAuditEntry), total: z.number().int() });
export const FixedAssetApiPath = {
    Capabilities: '/api/v1/capabilities',
    Users: '/api/v1/users',
    Sites: '/api/v1/sites',
    Site: '/api/v1/sites/:groupId',
    AssetTypes: '/api/v1/asset-types',
    AssetType: '/api/v1/asset-types/:id',
    Locations: '/api/v1/locations',
    Location: '/api/v1/locations/:id',
    EligibleManagers: '/api/v1/asset-managers',
    ReassignManager: '/api/v1/asset-managers/reassign',
    Assets: '/api/v1/assets',
    Asset: '/api/v1/assets/:code',
    AssetCard: '/api/v1/assets/:code/card',
    AssetUser: '/api/v1/assets/:code/user',
    AssetManager: '/api/v1/assets/:code/manager',
    AssetLocation: '/api/v1/assets/:code/location',
    AssetSite: '/api/v1/assets/:code/site',
    AssetStatus: '/api/v1/assets/:code/status',
    AssetDisposal: '/api/v1/assets/:code/disposal',
    AssetDisposalRevert: '/api/v1/assets/:code/disposal/revert',
    AssetHistory: '/api/v1/assets/:code/history',
    AssetAttachments: '/api/v1/assets/:code/attachments',
    AssetAttachment: '/api/v1/assets/:code/attachments/:id',
    AssetAttachmentContent: '/api/v1/assets/:code/attachments/:id/content',
    AssetInventoryItems: '/api/v1/assets/:code/inventory-items',
    MyAssets: '/api/v1/my/assets',
    MyInventoryItems: '/api/v1/my/inventory-items',
    AssetExport: '/api/v1/exports/assets',
    Inventories: '/api/v1/inventories',
    Inventory: '/api/v1/inventories/:id',
    InventoryItem: '/api/v1/inventories/:id/items/:code',
    InventoryClose: '/api/v1/inventories/:id/close',
    InventoryExport: '/api/v1/inventories/:id/export',
    ImportTemplate: '/api/v1/imports/template',
    ImportPreview: '/api/v1/imports/preview',
    Imports: '/api/v1/imports',
    AdminAssetManagers: '/api/v1/admin/asset-managers',
    AdminAssetManager: '/api/v1/admin/asset-managers/:sub',
    AuditLogs: '/api/v1/admin/audit-logs',
};
/** Fills `:name` placeholders of an API path. */
export function apiPath(path, params) {
    return path.replace(/:(\w+)/g, (placeholder, name) => {
        const value = params[name];
        return value === undefined ? placeholder : encodeURIComponent(String(value));
    });
}
//# sourceMappingURL=index.js.map